import { ErrorText } from "components/UI/common.style";
import React from "react";
import {
  Control,
  Controller,
  FieldValues,
  Path,
  PathValue,
} from "react-hook-form";
import styled from "styled-components";
import DateInputField from "../DateInputField";

export type DateRangeValue = {
  startDate: string;
  endDate: string;
};

export type DateRangeProps<TFieldValues extends FieldValues = FieldValues> = {
  name: Path<TFieldValues>;
  control: Control<TFieldValues, any>;
  label?: string;
  startDateLabel?: string;
  endDateLabel?: string;
  defaultValue?: DateRangeValue;
  required?: boolean;
  disabled?: boolean;
  minDate?: string;
  maxDate?: string;
  startDatePlaceholder?: string;
  endDatePlaceholder?: string;
  startDateMin?: string;
  startDateMax?: string;
  endDateMin?: string;
  endDateMax?: string;
  id?: string;
  inputLabelShrink?: boolean;
};

const DateRange = <TFieldValues extends FieldValues = FieldValues>({
  name,
  control,
  label,
  startDateLabel = "Start Date",
  endDateLabel = "End Date",
  defaultValue,
  required = false,
  disabled = false,
  minDate,
  maxDate,
  startDatePlaceholder = "Select start date",
  endDatePlaceholder = "Select end date",
  startDateMin,
  startDateMax,
  endDateMin,
  endDateMax,
  id,
  inputLabelShrink,
}: DateRangeProps<TFieldValues>) => {
  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue as PathValue<TFieldValues, Path<TFieldValues>>}
      render={({ field: { onChange, value }, formState: { errors } }) => {
        const errorMessage = errors[name]?.message ?? null;
        const hasError = typeof errors[name]?.message !== "undefined";

        const dateRange = value as DateRangeValue;
        const startDate = dateRange?.startDate ?? "";
        const endDate = dateRange?.endDate ?? "";

        // Validate that end date is after start date
        const isDateRangeValid = () => {
          if (!startDate || !endDate) return true;
          return startDate <= endDate;
        };

        const isRangeError =
          hasError || (!!startDate && !!endDate && !isDateRangeValid());
        const errorMessageText =
          (errorMessage as string) ||
          (isRangeError && "End date must be after start date");

        const handleStartDateChange = (
          e: React.ChangeEvent<HTMLInputElement>,
        ) => {
          const newStartDate = e.target.value;
          onChange({
            startDate: newStartDate,
            endDate: endDate,
          });
        };

        const handleEndDateChange = (
          e: React.ChangeEvent<HTMLInputElement>,
        ) => {
          const newEndDate = e.target.value;
          onChange({
            startDate: startDate,
            endDate: newEndDate,
          });
        };

        return (
          <>
            {errorMessage && typeof errorMessage === "string" && (
              <ErrorText>{errorMessage}</ErrorText>
            )}
            <Container>
              {label && <Label>{label}</Label>}

              <DateRangeContainer>
                <DateInputField
                  label={startDateLabel}
                  value={startDate}
                  onChange={handleStartDateChange}
                  error={isRangeError}
                  disabled={disabled}
                  required={required}
                  placeholder={startDatePlaceholder}
                  minDate={startDateMin || minDate}
                  maxDate={startDateMax || maxDate}
                  id={id ? `${id}-start` : undefined}
                  inputLabelShrink={inputLabelShrink || !!startDate}
                />

                <Separator>to</Separator>

                <DateInputField
                  label={endDateLabel}
                  value={endDate}
                  onChange={handleEndDateChange}
                  error={isRangeError}
                  disabled={disabled}
                  required={required}
                  placeholder={endDatePlaceholder}
                  minDate={endDateMin || minDate || startDate}
                  maxDate={endDateMax || maxDate}
                  id={id ? `${id}-end` : undefined}
                  inputLabelShrink={inputLabelShrink || !!endDate}
                />
              </DateRangeContainer>

              {errorMessageText && (
                <HelperText error={isRangeError}>{errorMessageText}</HelperText>
              )}
            </Container>
          </>
        );
      }}
    />
  );
};

const Container = styled.div`
  display: flex;
  // flex-direction: column;
  width: 100%;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  font-size: 0.875rem;
`;

const DateRangeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

const Separator = styled.span`
  color: #666;
  font-size: 0.9rem;
  padding: 0 0.25rem;
`;

const HelperText = styled.div<{ error?: boolean }>`
  margin-top: 0.25rem;
  font-size: 0.75rem;
  color: ${({ error }) => (error ? "#d32f2f" : "#666")};
`;

export default DateRange;
